// LocalVideoDisplay.jsx
import React, { useEffect, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { videoStorage, auth, db } from '../../../../firebase';
import ProcessingBar from '../../../../components/appComponents/ProcessingBar';
import useProcessingProgress from '../../../../components/appComponents/useProcessingProgress';
import './localWorkspace.css';
import './localVideoDisplay.css';
import placeholder_dark_thumbnail from '../../../../assets/images/thumbnail/placeholder-dark-thumbnail.jpg';

const LocalVideoDisplay = ({
  content_id,
  isLocalTranscriptAvailable,
  isLOCALProcessing,
  isTranscriptEmpty,
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoQuality, setVideoQuality] = useState('');
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [job, setJob] = useState(null); // the workspace doc while it processes
  const user = auth.currentUser;
  const percent = useProcessingProgress({ active: isLOCALProcessing, source: 'local', id: content_id, job });

  // 1) Fetch video URL
  useEffect(() => {
    if (!user) return;
    const fetchVideoUrl = async () => {
      const videoRef = ref(videoStorage, `${user.uid}/workspace-${content_id}`);
      try {
        const url = await getDownloadURL(videoRef);
        setVideoUrl(url);
      } catch (err) {
        console.error('Failed to load video URL', err);
      }
    };
    fetchVideoUrl();
  }, [user, content_id]);

  // 2) Fetch metadata (localStorage or Firestore)
  useEffect(() => {
    if (!isLocalTranscriptAvailable && !isLOCALProcessing) return;
    const localKey = `videometadata_${content_id}`;

    const fetchFromFirestore = async () => {
      if (!user) return;
      try {
        const userDoc = doc(db, 'users', user.uid);
        const wsDoc = doc(userDoc, 'workspaceContent', `workspace-${content_id}`);
        const snap = await getDoc(wsDoc);
        if (snap.exists()) {
          const data = snap.data();
          const meta = {
            video_file_name: data.video_file_name || '',
            video_quality: data.video_quality || '',
          };
          setVideoTitle(meta.video_file_name);
          setVideoQuality(meta.video_quality);
          localStorage.setItem(localKey, JSON.stringify(meta));
        }
      } catch (err) {
        console.error('Failed to fetch metadata', err);
      }
    };

    const cached = localStorage.getItem(localKey);
    if (cached) {
      try {
        const { video_file_name, video_quality } = JSON.parse(cached);
        setVideoTitle(video_file_name);
        setVideoQuality(video_quality);
      } catch {
        localStorage.removeItem(localKey);
        fetchFromFirestore();
      }
    } else {
      fetchFromFirestore();
    }
  }, [user, content_id, isLocalTranscriptAvailable, isLOCALProcessing]);

  // 3) While processing, follow the workspace doc (start time, duration, chunks) for the progress bar
  useEffect(() => {
    if (!user || !isLOCALProcessing) return undefined;
    const wsRef = doc(db, 'users', user.uid, 'workspaceContent', `workspace-${content_id}`);
    return onSnapshot(wsRef, snap => setJob(snap.exists() ? snap.data() : null));
  }, [user, content_id, isLOCALProcessing]);

  const truncate = (name) =>
    name.length > 28 ? `${name.slice(0, 28)}...` : name;
  const handleVideoLoaded = () => setIsVideoLoaded(true);

  return (
    <div className="localworks-video-section">
      {/* Video Preview */}
      <div className="localworks-video-preview">
        {videoUrl ? (
          <>
            {!isVideoLoaded && (
              <img
                src={placeholder_dark_thumbnail}
                alt="Video Thumbnail"
                className="localworks-video-preview-placeholder"
              />
            )}
            <video
              src={videoUrl}
              controls
              className="localworks-video-preview-media"
              onLoadedData={handleVideoLoaded}
              style={{ display: isVideoLoaded ? 'block' : 'none' }}
            />
          </>
        ) : (
          <img
            src={placeholder_dark_thumbnail}
            alt="Video Thumbnail"
            className="localworks-video-preview-placeholder"
          />
        )}
      </div>

      {/* Info / Bump Progress */}
      <div className="localworks-video-info">
        {isLOCALProcessing ? (
          <div className="localworks-bump-progress-wrapper">
            <ProcessingBar percent={percent} />
          </div>
        ) : (
          <>
            <div className="localworks-filename-container">
              <div className="localworks-filename-label">Filename</div>
              <div className="localworks-filename">
                {truncate(videoTitle)}
              </div>
            </div>
            <div className="localworks-quality-container">
              <div className="localworks-quality">{videoQuality}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LocalVideoDisplay;
